import { useSegmentViewEvents } from 'hooks/useSegmentView';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { MPosContext } from 'utils/context';
import { getImageBasePath } from 'utils/generics';
import {
    SegmentEventNameTypes,
    SegmentEventPageTypes,
    SegmentEventPages,
    PRODUCT,
    SUB_PRODUCT_NAME,
} from 'utils/segment/constants';
import ErrorPage from 'widgets/errorScreen';
import Header from 'widgets/header';
import AddNewSubDealer from './addNewSubDealer';
import DealerDetails from './dealerDetails';
import { ISubDealer } from './types';
import Confirmation from 'widgets/confirmation';
import SnackBar from 'library/snackbar';
import { ModalMobile, ModalDesktop } from '@acko-tech/building-blocks.ui.modal';
import { PageLoader } from 'utils/styles';
import { useResponsiveDevice } from 'hooks/useResponsiveDevice';
import { Permissions } from 'utils/constants';
import * as Styles from './styles';

function ManageDealers() {
    const { setShowHamburger, partnerId, permissions, permissionsMap } =
        useContext(MPosContext);

    const router = useRouter();

    const { isMobile } = useResponsiveDevice();

    const [subDealers, setSubDealers] = useState<ISubDealer[]>([]);
    const [selectedSubDealer, setSelectedSubDealer] = useState({
        name: '',
        phone: '',
        index: -1,
    });
    const [showAddNewSubDealer, setShowAddNewSubDealer] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [hasPermission, setHasPermission] = useState(
        permissionsMap[Permissions.ADD_SUB_DEALERS] ||
            permissions.permission_list.length > 0 ||
            false
    );
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [showErrorPage, setShowErrorPage] = useState(false);
    const { group } = permissions;

    /**
     * view segment hook
     */
    useSegmentViewEvents(SegmentEventNameTypes.VIEW, {
        page_url: typeof window !== 'undefined' ? window.location.href : '',
        page_type: SegmentEventPageTypes.MAIN_PAGE,
        view_name: SegmentEventPages.VIEW_ADD_REMOVE_SUB_DEALER,
        product: PRODUCT,
        sub_product_name: SUB_PRODUCT_NAME,
        platform: isMobile ? 'mweb' : 'web',
        referrer: document.referrer || '',
        device: navigator.userAgent || '',
        partner: partnerId,
    });

    useEffect(() => {
        setHasPermission(
            permissionsMap[Permissions.ADD_SUB_DEALERS] ||
                permissions.permission_list.length > 0 ||
                false
        );
    }, [permissionsMap]);

    useEffect(() => {
        if (!showAddNewSubDealer) {
            getSubDealers();
        }
    }, [showAddNewSubDealer]);

    const getSubDealers = () => {
        import('./apis').then(async (req) => {
            const { status, statusCode, data } = await req.getSubDealers(
                partnerId,
                group
            );
            if (statusCode === 401) {
                router.push(`/login`);
                setSnackbar({
                    type: 'failure',
                    message: 'Token expired. Redirecting to login page.',
                });
                return;
            }
            if (status && data) {
                const { sub_dealers } = data;
                setSubDealers(sub_dealers || []);
                setShowErrorPage(false);
            } else {
                setShowErrorPage(true);
            }
            setIsPageLoading(false);
        });
    };

    const onDeleteSubDealer = (index: number, name: string, phone: string) => {
        setShowDeleteModal(true);
        setSelectedSubDealer({
            name,
            phone,
            index,
        });
    };

    const onConfirmDelete = (_e, action: 'yes' | 'no') => {
        setShowDeleteModal(false);
        if (action === 'no') {
            return;
        }
        import('./apis').then(async (req) => {
            const { status, statusCode } = await req.removeSubDealers(
                partnerId,
                selectedSubDealer.phone,
                group
            );
            if (statusCode === 401) {
                router.push(`/login`);
                setSnackbar({
                    type: 'failure',
                    message: 'Token expired. Redirecting to login page.',
                });
                return;
            }
            if (status) {
                setSnackbar({
                    type: 'success',
                    message: 'Sub-dealer successfully deleted',
                });
                const subDealersCopy = Object.assign([], subDealers);
                subDealersCopy.splice(selectedSubDealer.index, 1);
                setSubDealers(subDealersCopy);
                return;
            }
            setSnackbar({
                type: 'failure',
                message: 'Oops. Something went wrong.',
            });
        });
    };

    const onBack = () => {
        setShowHamburger(true);
    };

    const onClickAddSubDealer = () => {
        if (subDealers?.length > 2) {
            setSnackbar({
                type: 'failure',
                message: 'You can only add upto 3 sub dealers',
            });
            return;
        }
        setShowAddNewSubDealer(true);
    };

    const checkPermission = (p: Permissions) => {
        const index = permissions.permission_list.findIndex(
            (permission) => permission === p
        );
        return index !== -1;
    };

    const Modal: any = isMobile ? ModalMobile : ModalDesktop;

    if (isPageLoading) {
        return <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />;
    }

    if (!hasPermission || showErrorPage) {
        return (
            <ErrorPage
                errorMessage={
                    showErrorPage
                        ? ''
                        : `You don't have permission to view this page`
                }
            />
        );
    }

    return (
        <Styles.Wrapper>
            <Header />
            {showAddNewSubDealer ? (
                <AddNewSubDealer
                    subDealers={subDealers}
                    setSubDealers={setSubDealers}
                    setShowAddNewSubDealer={setShowAddNewSubDealer}
                />
            ) : (
                <>
                    <Styles.Header>
                        <Styles.BackArrowIcon>
                            <Image
                                width={'10px'}
                                height={'24px'}
                                alt={''}
                                src={getImageBasePath('/icons/backArrow.svg')}
                                onClick={onBack}
                            />
                        </Styles.BackArrowIcon>
                    </Styles.Header>
                    <Styles.AddRemoveDealerText>
                        Add/ remove sub-dealer
                    </Styles.AddRemoveDealerText>
                    {subDealers?.map((dealer: ISubDealer, index: number) => (
                        <Styles.SubDealerDetails key={dealer.phone}>
                            <DealerDetails
                                index={index}
                                name={dealer.name}
                                phone={dealer.phone}
                                onClick={onDeleteSubDealer}
                                hasPermission={checkPermission(
                                    Permissions.REMOVE_SUB_DEALERS
                                )}
                            />
                        </Styles.SubDealerDetails>
                    ))}
                    {checkPermission(Permissions.ADD_SUB_DEALERS) && (
                        <Styles.AddSubDealerFlex onClick={onClickAddSubDealer}>
                            <Styles.IconFlex>
                                <Image
                                    width={'24px'}
                                    height={'24px'}
                                    alt={''}
                                    src={
                                        subDealers?.length < 3
                                            ? getImageBasePath(
                                                  '/icons/greenPlus.svg'
                                              )
                                            : getImageBasePath(
                                                  '/icons/grayPlus.svg'
                                              )
                                    }
                                />
                            </Styles.IconFlex>
                            <Styles.AddSubDealer>
                                Add new sub-dealer
                            </Styles.AddSubDealer>
                        </Styles.AddSubDealerFlex>
                    )}
                </>
            )}
            <Modal
                header={<></>}
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                padding={'20px'}
                wrapperStyles={`border-radius: 16px 16px 0px 0px;`}
                showOverlayBackground={true}
            >
                <Confirmation
                    image={'/images/alert.svg'}
                    text={`Are you sure you want to remove the sub-dealer?`}
                    subText={selectedSubDealer.name}
                    onClick={onConfirmDelete}
                />
            </Modal>
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() => {
                    setSnackbar({
                        type: '',
                        message: '',
                    });
                }}
            />
        </Styles.Wrapper>
    );
}

export default ManageDealers;
